import React, { useState, useEffect, useContext, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "api/axiosInstance";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import { Grid, Divider, IconButton, InputAdornment, Icon } from "@mui/material";

import "./index.css";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import SignInLayout from "layouts/authentication/components/SignInLayout";
import { LanguageContext } from "../../../context/LanguageContext";
import { COLORS, TYPO } from "theme/tokens";

export default function ResetPassword() {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token");
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [status, setStatus] = useState({ loading: false });

  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("reset_password", "authentication/reset-password/reset-password_lang");
  }, [language]);
  const LANGUAGE = useMemo(
    () => componentLanguageData.reset_password || { Main: {}, Alert: {}, Passwod_score: {} },
    [componentLanguageData]
  );

  const passwordValid =
    /^(?=.*[A-Z])(?=.*\d)[A-Za-z\d!@#$%^&*()_\-+=\[\]{};:'",.<>/?\\|`~]{8,}$/.test(password);

  useEffect(() => {
    if (status.loading) document.body.classList.add("loading");
    else document.body.classList.remove("loading");
    return () => document.body.classList.remove("loading");
  }, [status.loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordValid) {
      toast.error(LANGUAGE.Alert.Content1);
      return;
    }
    if (password !== confirmPassword) {
      toast.error(LANGUAGE.Main.Content9);
      return;
    }
    setStatus({ loading: true });

    try {
      const res = await axiosInstance.post("/auth/reset-password", { token, password });
      if (res.data.message === "Success_forget_002") {
        toast.success(LANGUAGE.Alert.Content2);
        setTimeout(() => navigate("/authentication/sign-in"), 2000);
      } else {
        toast.warning(LANGUAGE.Alert.Content3);
      }
    } catch (err) {
      toast.error(LANGUAGE.Alert.Content4);
    } finally {
      setStatus({ loading: false });
    }
  };

  // 依新版設計稿（與忘記密碼頁一致）：bg #F6F6F6 / radius 6px / 內文 bodyM #000 / placeholder bodyM #C4C4C4
  const filledInputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: COLORS.grayLightest,
      borderRadius: "6px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused": {
        backgroundColor: COLORS.white,
        "& fieldset": { border: "1px solid", borderColor: COLORS.main },
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 8px 8px 8px",
      ...TYPO.bodyM,
      color: COLORS.black,
      "&::placeholder": { ...TYPO.bodyM, color: COLORS.grayMedian, opacity: 1 },
    },
  };

  const cardLogo =
    language === "zh-TW" || language === "zh-CN"
      ? "/images/logo/logo_2_1.png"
      : "/images/logo/logo_2_2.png";

  return (
    <SignInLayout>
      <Card
        sx={{
          width: "100%",
          maxWidth: 920,
          minHeight: { xs: "auto", md: 600 },
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}
      >
        <Grid container sx={{ minHeight: "inherit" }}>
          {/* === 左欄：品牌（白底 + 右框線 + logo 162×254 置中）=== */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: COLORS.white,
              borderRight: `1px solid ${COLORS.grayLighter}`,
              px: 10,
              py: 8,
            }}
          >
            <MDBox component="img" src={cardLogo} alt="MASQuant" sx={{ width: 162, height: 254, objectFit: "contain" }} />
          </Grid>

          {/* === 右欄：表單 === */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              background: COLORS.white,
              p: { xs: 3, sm: 5, md: 8 },
            }}
          >
            <MDBox sx={{ width: "100%", maxWidth: 320 }}>
              {/* 手機版 logo */}
              <MDBox display={{ xs: "flex", md: "none" }} justifyContent="center" mb={3}>
                <MDBox component="img" src={cardLogo} alt="MASQuant" sx={{ maxHeight: 120, objectFit: "contain" }} />
              </MDBox>

              {/* 重設密碼 — displayM / #000 / 500 */}
              <MDTypography sx={{ ...TYPO.displayM, color: COLORS.black, fontWeight: 500, mb: 3 }}>
                {LANGUAGE.Main.Content1}
              </MDTypography>

              <MDBox component="form" onSubmit={handleSubmit}>
                {/* 新密碼* — bodyS / #000 / 400 */}
                <MDBox mb={2}>
                  <MDTypography sx={{ ...TYPO.bodyS, color: COLORS.black, fontWeight: 400, display: "block", mb: 1 }}>
                    {LANGUAGE.Main.Content2}
                    <span style={{ ...TYPO.bodyS, color: COLORS.main }}>*</span>
                  </MDTypography>
                  <MDInput
                    type={showPassword ? "text" : "password"}
                    placeholder={LANGUAGE.Main.Content6}
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={filledInputSx}
                    InputProps={
                      password.length > 0
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword((v) => !v)}
                                  edge="end"
                                  size="small"
                                  aria-label="toggle password visibility"
                                  tabIndex={-1}
                                >
                                  <Icon fontSize="small">{showPassword ? "visibility" : "visibility_off"}</Icon>
                                </IconButton>
                              </InputAdornment>
                            ),
                          }
                        : undefined
                    }
                  />
                </MDBox>

                {/* 確認新密碼* — 與新密碼一致才放行 */}
                <MDBox mb={2}>
                  <MDTypography sx={{ ...TYPO.bodyS, color: COLORS.black, fontWeight: 400, display: "block", mb: 1 }}>
                    {LANGUAGE.Main.Content7}
                    <span style={{ ...TYPO.bodyS, color: COLORS.main }}>*</span>
                  </MDTypography>
                  <MDInput
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={LANGUAGE.Main.Content8}
                    fullWidth
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    sx={filledInputSx}
                    error={confirmPassword.length > 0 && password !== confirmPassword}
                    InputProps={
                      confirmPassword.length > 0
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowConfirmPassword((v) => !v)}
                                  edge="end"
                                  size="small"
                                  aria-label="toggle password visibility"
                                  tabIndex={-1}
                                >
                                  <Icon fontSize="small">{showConfirmPassword ? "visibility" : "visibility_off"}</Icon>
                                </IconButton>
                              </InputAdornment>
                            ),
                          }
                        : undefined
                    }
                  />
                  {confirmPassword.length > 0 && password !== confirmPassword && (
                    <MDTypography variant="caption" sx={{ color: COLORS.error, mt: 0.5, display: "block" }}>
                      {LANGUAGE.Main.Content9}
                    </MDTypography>
                  )}
                </MDBox>

                {/* 確認 — 高 60 / radius 16 / 漸層 / title / #FFF / 500 */}
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  fullWidth
                  disabled={status.loading || !password || !confirmPassword || password !== confirmPassword}
                  sx={{
                    height: 60,
                    borderRadius: "16px",
                    background: COLORS.gradient,
                    boxShadow: "4px 4px 9px 0 rgba(222, 86, 15, 0.20)",
                    textTransform: "none",
                    ...TYPO.title,
                    color: `${COLORS.white} !important`,
                    fontWeight: 500,
                    "&:hover": {
                      background: COLORS.gradient,
                      boxShadow: "4px 4px 12px 0 rgba(222, 86, 15, 0.30)",
                    },
                    "&:disabled": {
                      background: COLORS.gradient,
                      color: `${COLORS.white} !important`,
                      opacity: 0.6,
                    },
                  }}
                >
                  {status.loading ? LANGUAGE.Alert.Content5 : LANGUAGE.Alert.Content6}
                </MDButton>

                {/* 分隔線 */}
                <Divider sx={{ my: 3, borderColor: COLORS.grayLighter }} />

                {/* 想起密碼了嗎（bodyM/#000/400） + 回到登入（icon + title/#F36D26/500）*/}
                <MDBox display="flex" flexDirection="column" alignItems="center" gap={1}>
                  <MDTypography sx={{ ...TYPO.bodyM, color: COLORS.black, fontWeight: 400 }}>
                    {LANGUAGE.Main.Content4}
                  </MDTypography>
                  <Link
                    to="/authentication/sign-in"
                    className="auth-underline"
                    style={{ display: "flex", alignItems: "center", gap: 6, textDecoration: "none" }}
                  >
                    <img src="/images/icon/Vector-Left.png" alt="" style={{ width: 14, height: 14, objectFit: "contain" }} />
                    <span style={{ ...TYPO.title, color: COLORS.main, fontWeight: 500 }}>{LANGUAGE.Main.Content5}</span>
                  </Link>
                </MDBox>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </Card>

      {status.loading && (
        <div className="loading-overlay">
          <CircularProgress color="inherit" />
        </div>
      )}
    </SignInLayout>
  );
}
